import React, { useEffect, useMemo, useState } from 'react';
import { UserCog, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissao } from '../../hooks/usePermissao';
import { Select } from '../../Components/ui';
import { API_URL } from '../../api/config';

// Tela enxuta pra coordenador ligar uma conta de login (perfil "professor") ao
// cadastro de professor usado nas turmas — o mesmo vínculo que Admin Usuários
// já faz, mas sem dar acesso a criar/editar/excluir usuários, aprovar
// cadastros ou mexer em instituições/perfis (isso continua master-only). O
// backend (auth.js, rotas /vincular-professor/*) já restringe cada ação às
// instituições do próprio coordenador, então esta tela só precisa consumir.
function VincularProfessor() {
  const { token, user } = useAuth();
  const { podeEditar } = usePermissao('/vincular-professor');
  const [usuarios, setUsuarios] = useState([]);
  const [professoresPorInstituicao, setProfessoresPorInstituicao] = useState({});
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [mensagem, setMensagem] = useState('');
  const [salvandoId, setSalvandoId] = useState(null);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const carregar = async () => {
    setLoading(true);
    setErro('');
    try {
      const resUsuarios = await fetch(`${API_URL}/api/auth/vincular-professor/usuarios`, {
        headers,
      });
      const rawUsuarios = await resUsuarios.json();
      const dataUsuarios = rawUsuarios?.data ?? (Array.isArray(rawUsuarios) ? rawUsuarios : []);
      if (!resUsuarios.ok) throw new Error(rawUsuarios?.error || 'Erro ao carregar usuários.');

      const idsInstituicoes = [...new Set(dataUsuarios.flatMap((u) => u.instituicoes))];
      const mapa = {};
      await Promise.all(
        idsInstituicoes.map(async (id) => {
          const res = await fetch(
            `${API_URL}/api/auth/vincular-professor/professores?id_instituicao=${id}`,
            { headers },
          );
          const rawProfs = res.ok ? await res.json() : [];
          mapa[id] = rawProfs?.data ?? (Array.isArray(rawProfs) ? rawProfs : []);
        }),
      );

      setUsuarios(dataUsuarios);
      setProfessoresPorInstituicao(mapa);
    } catch (error) {
      setErro(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const opcoesProfessor = (usuario) => {
    const vistos = new Set();
    const opcoes = [];
    for (const idInst of usuario.instituicoes) {
      for (const p of professoresPorInstituicao[idInst] || []) {
        if (!vistos.has(p.id)) {
          vistos.add(p.id);
          opcoes.push({ value: p.id, label: p.nome });
        }
      }
    }
    return opcoes;
  };

  const handleVincular = async (usuarioId, idProfessor) => {
    setSalvandoId(usuarioId);
    setErro('');
    setMensagem('');
    try {
      const res = await fetch(`${API_URL}/api/auth/vincular-professor/usuarios/${usuarioId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({ id_professor: idProfessor || null }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erro ao atualizar vínculo.');
      setMensagem('Vínculo atualizado com sucesso.');
      await carregar();
    } catch (error) {
      setErro(error.message);
    } finally {
      setSalvandoId(null);
    }
  };

  return (
    <div className="min-h-screen w-full bg-[#F9FAFB] dark:bg-gray-950">
      <main className="p-4 max-w-4xl mx-auto space-y-6">
        {erro && (
          <div className="p-4 rounded-2xl bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-900/40 text-red-700 dark:text-red-400 text-sm flex items-start gap-3">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            {erro}
          </div>
        )}
        {mensagem && (
          <div className="p-4 rounded-2xl bg-green-50 dark:bg-green-900/20 border border-green-100 dark:border-green-900/40 text-green-700 dark:text-green-400 text-sm flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
            {mensagem}
          </div>
        )}

        <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm">
          <div className="px-3 py-2 border-b border-gray-100 dark:border-gray-800 flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center">
              <UserCog className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                Vincular Educador
              </h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Liga a conta de login de um educador ao cadastro de educador usado nas turmas — sem
                isso, telas como "Grade" não sabem quais turmas são dele.
                {user?.perfil === 'coordenador' && ' Mostrando só as contas das suas instituições.'}
              </p>
            </div>
          </div>
          <div className="p-3">
            {loading ? (
              <div className="flex items-center justify-center py-12 text-gray-500 dark:text-gray-400">
                <Loader2 className="w-5 h-5 animate-spin mr-3" />
                <span className="text-sm">Carregando...</span>
              </div>
            ) : usuarios.length === 0 ? (
              <div className="text-center py-12 text-gray-500 dark:text-gray-400 text-sm">
                Nenhuma conta de educador encontrada.
              </div>
            ) : (
              <div className="space-y-3">
                {usuarios.map((u) => (
                  <div
                    key={u.id}
                    className="p-2 rounded-2xl border border-gray-100 dark:border-gray-800 bg-gray-50/30 dark:bg-gray-800/30 flex flex-col sm:flex-row sm:items-center gap-3"
                  >
                    <div className="flex-1 min-w-0">
                      <div
                        className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate"
                        translate="no"
                      >
                        {u.nome}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {u.email}
                      </div>
                    </div>
                    <div className="w-full sm:w-64">
                      <Select
                        size="sm"
                        value={u.id_professor || ''}
                        disabled={salvandoId === u.id || !podeEditar}
                        onChange={(e) =>
                          handleVincular(u.id, e.target.value ? Number(e.target.value) : null)
                        }
                        options={[
                          { value: '', label: 'Nenhum (sem vínculo)' },
                          ...opcoesProfessor(u),
                        ]}
                      />
                      {!u.id_professor && (
                        <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                          Sem vínculo — turmas não aparecerão na Grade dele
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

export default VincularProfessor;
